import { createSelector } from 'reselect';

import {
	BaggageType,
	CheckinOrderFragment,
	CheckinOrderStatus,
	CheckoutOrderFragment,
	FlightFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment as BaggageServiceFragment,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment as MealServiceFragment,
	GdsService_OrderAdditionalServiceGdsServiceSeat_Fragment as SeatServiceFragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment as VipServiceFragment,
	GdsServiceVipSubType,
	IncludedGdsServicesFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderPriceFragment,
	OrderStatus,
	PassengerFragment,
	PricePartsFragment,
	SegmentFragment,
	TravellerCardFfpInfo,
	TravellerFieldEnum
} from '@websky/graphql';
import { Currency } from '../../../../enums';
import { Baggage, SelectedBaggageItem } from '../../../../BaggageService/types';
import { RecommendedMeal } from '../../../../ServiceRecommendation/types';
import { Meal } from '../../../../Meal/types';
import { MoneyType } from '../../../../Money';
import { SelectedServicesState } from '../../../../Checkout/store/selectedServices/reducers';

import { getTotalPrice as getTotalPriceUtil } from '@shared/lib/money';
import {
	getHasRequestedServices,
	getSeatMapUniqueAvailableSegments,
	getTravellerName,
	getUserValue
} from '../../../../utils';
import { convertBaggageObject, convertMealObject } from '../../../../Checkout/types';
import { checkoutSelectedServicesStateFactory } from '../../../../Checkout/utils';

import { OrderServiceType } from '../model/types';
import { getOrderServiceType } from './getOrderServiceType';
import { convertIncludedBaggageToSelected } from './convertIncludedBaggageToSelected';
import { getExistUserType } from '@shared/lib/passenger';
import { buildGdsServicesById } from '@shared/lib/gdsService';

type OrderFragment = CheckoutOrderFragment | CheckinOrderFragment;
type GdsServiceFragment = OrderFragment['additionalServices']['gdsServices']['services'][0];
type OrderPricePartsFragment = OrderPriceFragment | PricePartsFragment;
type OrderPricePartsKeys = keyof (OrderPriceFragment & PricePartsFragment);

function isCheckoutOrder(order: CheckoutOrderFragment | CheckinOrderFragment): order is CheckoutOrderFragment {
	return (order as CheckoutOrderFragment).flight !== undefined;
}

function isIncludeGdsServiceType(types: OrderAdditionalServiceGdsServiceServiceType[]) {
	return function (service: GdsServiceFragment) {
		return types.includes(service.type);
	};
}

function isIncludeBaggageType(types: BaggageType[]) {
	return function (baggage: BaggageServiceFragment) {
		if (!types.length) {
			return false;
		}

		return types.includes(baggage.baggageType);
	};
}

function getBaggageServiceByType(types: BaggageType[]) {
	return function (baggage: Baggage) {
		if (!types.length) {
			return false;
		}

		return types.includes(baggage.type);
	};
}

function filterBaggageServicesByTypes(types: BaggageType[]) {
	return function (services: Baggage[]) {
		return services.filter(getBaggageServiceByType(types));
	};
}

function isIncludeVipSubType(subTypes: GdsServiceVipSubType[]) {
	return function (vipService: VipServiceFragment) {
		if (!subTypes.length) {
			return false;
		}

		return subTypes.includes(vipService.subType);
	};
}

function getPriceToPayPart(part: OrderPricePartsKeys) {
	return function (priceToPay: OrderPricePartsFragment, currency: Currency) {
		const priceByPart = priceToPay[part];

		if (!priceByPart) {
			return { amount: 0, currency };
		}

		return { ...priceByPart, currency };
	};
}

export interface CommonOrderSelectors<T> {
	// basic
	getOrder: (state: T) => OrderFragment;
	getOrderId: (state: T) => string;
	getStatus: (state: T) => OrderFragment['status'];
	getFlight: (state: T) => FlightFragment;
	getSegments: (state: T) => SegmentFragment[];
	getTravellers: (state: T) => PassengerFragment[];
	getTravellersWithVisa: (state: T) => CheckinOrderFragment['travellers'];
	getTravellersNames: (state: T) => Array<{ id: string; name: string }>;
	getSeatMap: (state: T) => FlightFragment['seatMap'];
	getSeatMapAvailableSegments: (state: T) => string[];
	getMessages: (state: T) => OrderFragment['messages'];
	getSsrData: (state: T) => CheckoutOrderFragment['ssrData'] | undefined;
	getFfpInfo: (state: T) => CheckoutOrderFragment['ffpInfo'] | undefined;
	getFfpBonusForFlight: (state: T) => NonNullable<CheckoutOrderFragment['ffpInfo']>['bonusForFlight'] | undefined;
	getFfpBonusByTravellers: (state: T) => Record<string, TravellerCardFfpInfo>;
	// price
	getPrice: (state: T) => OrderFragment['price'];
	getPriceToPay: (state: T) => OrderFragment['priceToPay'];
	getPriceBreakdown: (state: T) => OrderFragment['priceBreakdown'];
	getCurrency: (state: T) => Currency;
	getMilesPrice: (state: T) => OrderPriceFragment['totalInMiles'];
	getSeatPrice: (state: T) => MoneyType;
	getBaggagePrice: (state: T) => MoneyType;
	getMealPrice: (state: T) => MoneyType;
	getInsurancePrice: (state: T) => MoneyType;
	getAeroexpressPrice: (state: T) => MoneyType;
	getExtrasPrice: (state: T) => MoneyType;
	getBusinessLoungePrice: (state: T) => MoneyType;
	getVipPrice: (state: T) => MoneyType;
	getAirTicketPrice: (state: T) => MoneyType;
	getChargePrice: (state: T) => MoneyType;
	getTotalPrice: (state: T) => MoneyType;
	// services
	getSelectedServices: (state: T) => SelectedServicesState;
	getAdditionalServices: (state: T) => OrderFragment['additionalServices'];
	getGdsServices: (state: T) => GdsServiceFragment[];
	getGdsServicesById: (state: T) => Record<string, GdsServiceFragment>;
	getSeatServices: (state: T) => SeatServiceFragment[];
	getBaggageServices: (state: T) => BaggageServiceFragment[];
	getBaggagePetServices: (state: T) => BaggageServiceFragment[];
	getConvertedBaggageServices: (state: T) => Baggage[];
	getConvertedBaggageIncludedServices: (state: T) => SelectedBaggageItem[];
	getUpgradeBaggageServices: (state: T) => Baggage[];
	getAvailableUpgradeBaggageServices: (state: T) => Baggage[];
	getAdditionalBaggageServices: (state: T) => Baggage[];
	getSpecialBaggageServices: (state: T) => Baggage[];
	getOverweightBaggageServices: (state: T) => Baggage[];
	getUpgradableBaggageServices: (state: T) => Baggage[];
	getMealServices: (state: T) => MealServiceFragment[];
	getConvertedMealServices: (state: T) => Meal[];
	getMealRecommendedServices: (state: T) => RecommendedMeal[];
	getVipServices: (state: T) => VipServiceFragment[];
	getBaggageDeliveryServices: (state: T) => VipServiceFragment[];
	getPriorityBoardingServices: (state: T) => VipServiceFragment[];
	getCapsuleHotelServices: (state: T) => VipServiceFragment[];
	getExtraSeatServices: (state: T) => VipServiceFragment[];
	getBusinessLoungeServices: (state: T) => GdsServiceFragment[];
	hasSeatServices: (state: T) => boolean;
	// special services
	getEveryPortServices: (state: T) => OrderFragment['additionalServices']['nas'];
	getInsurances: (state: T) => OrderFragment['additionalServices']['insurances'];
	getTransfer: (state: T) => OrderFragment['additionalServices']['transfer'];
	getAeroexpress: (
		state: T
	) => NonNullable<OrderFragment['additionalServices']['transfer']>['aeroexpress'] | undefined;
	// flags
	getIsOrderNew: (state: T) => boolean;
	getIsOrderBooked: (state: T) => boolean;
	getIsOrderStarted: (state: T) => boolean;
	getIsOrderConfirmed: (state: T) => boolean;
	getIsOrderCancelled: (state: T) => boolean;
	getIsOrderExpired: (state: T) => boolean;
	getIsOrderProblematic: (state: T) => boolean;
	getIsContactsInfoEmpty: (state: T) => boolean;
	getIsTravellersDataFilled: (state: T) => boolean;
	getIsBlockedForPayment: (state: T) => boolean;
	getIsSkipSeatSelectionAllowed: (state: T) => boolean;
	getIsFfpSupported: (state: T) => boolean;
	getIsSeatMapUnavailable: (state: T) => boolean;
	getIsAllTravellersHavePreSeating: (state: T) => boolean;
	getIsConfirmedOrPreSeating: (state: T) => boolean;
	getIsAvailableSeats: (state: T) => boolean;
	getIsInsuranceSeparateStep: (state: T) => boolean;
	getHasRequestedOrderServices: (state: T) => boolean;
}

export function createCommonOrderSelectors<T>(getOrder: (state: T) => OrderFragment): CommonOrderSelectors<T> {
	// basic

	const getOrderId = createSelector(getOrder, order => order.id);

	const getStatus = createSelector(getOrder, order => order.status);

	const getFlight = createSelector(
		getOrder,
		(order): FlightFragment => {
			if (isCheckoutOrder(order)) {
				return order.flight;
			}

			return {
				seatMap: order.seatMap,
				segments: order.segments.map(segment => ({ segment: { ...segment, messages: [] } })),
				segmentGroups: [],
				fares: []
			};
		}
	);

	const getSegments = createSelector(getOrder, (order): SegmentFragment[] => {
		if (isCheckoutOrder(order)) {
			return order.flight.segments.map(({ segment }) => segment);
		}

		return order.segments.map(segment => ({ ...segment, messages: [] }));
	});

	const getTravellers = createSelector(getOrder, order => {
		if (isCheckoutOrder(order)) {
			return order.travellers;
		}

		return order.travellers.map<PassengerFragment>(traveller => ({
			...traveller,
			fares: [],
			tickets: [],
			needOthsSsr: false
		}));
	});

	const getTravellersWithVisa = createSelector(getOrder, order => {
		if (isCheckoutOrder(order)) {
			return [];
		}

		return order.travellers.filter(traveller => traveller.visaRequired && traveller.visaValues?.length);
	});

	const getTravellersNames = createSelector(getTravellers, travellers => {
		return travellers.map(traveller => ({ id: traveller.id, name: getTravellerName(traveller) }));
	});

	const getSeatMap = createSelector(getOrder, order =>
		isCheckoutOrder(order) ? order.flight.seatMap : order.seatMap
	);

	const getSeatMapAvailableSegments = createSelector(getSeatMap, seatMap => {
		return getSeatMapUniqueAvailableSegments(seatMap.segments);
	});

	const getMessages = createSelector(getOrder, order => order.messages);

	const getSsrData = createSelector(getOrder, order => (isCheckoutOrder(order) ? order.ssrData : undefined));

	const getFfpInfo = createSelector(getOrder, order => (isCheckoutOrder(order) ? order.ffpInfo : undefined));

	const getFfpBonusForFlight = createSelector(getFfpInfo, ffpInfo => ffpInfo?.bonusForFlight);

	const getFfpBonusByTravellers = createSelector(getFfpInfo, getTravellers, (ffpInfo, travellers) => {
		const travellersWithBonus: Record<string, TravellerCardFfpInfo> = {};

		const travellerCardFfpInfoMap = new Map(
			ffpInfo.bonusByCard?.filter(b => b.cardNumber).map(b => [b.cardNumber!, b]) || []
		);

		for (const traveller of travellers) {
			if (!getExistUserType(traveller, TravellerFieldEnum.LoyaltyNumber)) {
				continue;
			}

			const loyaltyNumber = getUserValue(traveller, TravellerFieldEnum.LoyaltyNumber);
			const cardFfpInfo = travellerCardFfpInfoMap.get(loyaltyNumber);

			travellersWithBonus[traveller.id] = {
				cardNumber: cardFfpInfo?.cardNumber,
				bonusForFlight: cardFfpInfo?.bonusForFlight
			};
		}

		return travellersWithBonus;
	});

	// price

	const getPrice = createSelector(getOrder, order => order.price);

	const getPriceToPay = createSelector(getOrder, order => order.priceToPay);

	const getPriceBreakdown = createSelector(getOrder, order => order.priceBreakdown);

	const getCurrency = createSelector(getPriceToPay, priceToPay => priceToPay.total?.currency as Currency);

	const getMilesPrice = createSelector(getPrice, price => (price as OrderPriceFragment).totalInMiles);

	const getSeatPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('seats'));

	const getBaggagePrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('baggage'));

	const getMealPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('meals'));

	const getInsurancePrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('insurance'));

	const getAeroexpressPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('aeroexpress'));

	const getExtrasPrice = createSelector(
		getInsurancePrice,
		getAeroexpressPrice,
		(insurancePrice, aeroexpressPrice) => {
			return getTotalPriceUtil([insurancePrice, aeroexpressPrice], price => price);
		}
	);

	const getBusinessLoungePrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('businessLounge'));

	const getVipPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('vip'));

	const getAirTicketPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('airticket'));

	const getChargePrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('charge'));

	const getTotalPrice = createSelector(getPriceToPay, getCurrency, getPriceToPayPart('total'));

	// services

	const getSelectedServices = createSelector(getOrder, checkoutSelectedServicesStateFactory);

	const getAdditionalServices = createSelector(getOrder, order => order.additionalServices);

	const getGdsServices = createSelector(
		getAdditionalServices,
		additionalServices => additionalServices.gdsServices?.services ?? []
	);

	const getGdsServicesById = createSelector(getGdsServices, buildGdsServicesById);

	const getIncludedServices = createSelector(getTravellers, travellers => {
		const includedServices: Partial<Record<OrderServiceType, Record<string, IncludedGdsServicesFragment[]>>> = {};

		for (const traveller of travellers) {
			const travellerId = traveller.id;
			const travellerIncludedServices = traveller.services?.brandIncludedServices?.services;

			if (!travellerIncludedServices?.length) {
				continue;
			}

			for (const includedService of travellerIncludedServices) {
				const serviceType = getOrderServiceType(includedService.service);

				if (!['Baggage', 'BaggagePet', 'BaggageDelivery', 'Meal'].includes(serviceType)) {
					continue;
				}

				if (!includedServices[serviceType]) {
					includedServices[serviceType] = {};
				}

				if (!includedServices[serviceType][travellerId]) {
					includedServices[serviceType][travellerId] = [];
				}

				includedServices[serviceType][travellerId].push(includedService);
			}
		}

		return includedServices;
	});

	const getSeatServices = createSelector(getGdsServices, gdsServices => {
		return gdsServices.filter(
			isIncludeGdsServiceType([OrderAdditionalServiceGdsServiceServiceType.Seat])
		) as SeatServiceFragment[];
	});

	const getBaggageServices = createSelector(getGdsServices, gdsServices => {
		return gdsServices.filter(
			isIncludeGdsServiceType([OrderAdditionalServiceGdsServiceServiceType.Baggage])
		) as BaggageServiceFragment[];
	});

	const getBaggagePetServices = createSelector(getBaggageServices, baggageServices => {
		return baggageServices.filter(
			isIncludeBaggageType([BaggageType.PetInCabin, BaggageType.PetInHold, BaggageType.PetInHandCarrier])
		);
	});

	const getConvertedBaggageServices = createSelector(getBaggageServices, baggageServices => {
		return baggageServices.map(convertBaggageObject);
	});

	const getConvertedBaggageIncludedServices = createSelector(getIncludedServices, includedServices => {
		const includedBaggage: SelectedBaggageItem[] = [];
		const includedBaggageServices = includedServices['Baggage'];

		if (!includedBaggageServices) {
			return [];
		}

		for (const [travellerId, includedBaggageByTraveller] of Object.entries(includedBaggageServices)) {
			for (const included of Object.values(includedBaggageByTraveller)) {
				includedBaggage.push(convertIncludedBaggageToSelected(travellerId, included));
			}
		}

		return includedBaggage;
	});

	const getUpgradeBaggageServices = createSelector(
		getConvertedBaggageServices,
		filterBaggageServicesByTypes([BaggageType.HandLuggageExcess])
	);

	const getAvailableUpgradeBaggageServices = createSelector(
		getUpgradeBaggageServices,
		getTravellers,
		(upgradeBaggage, travellers) => {
			const travellersByIdMap = new Map(
				travellers.map(traveller => {
					if (!traveller.services.gdsServices?.services?.length) {
						return [traveller.id, null];
					}

					const travellerGdsServicesById = new Map(
						traveller.services.gdsServices.services.map(service => [
							service.serviceId,
							service.count > service.confirmedCount
						])
					);

					return [traveller.id, travellerGdsServicesById];
				})
			);

			const isCanBeAddedOrRemoved = (item: Baggage) => {
				const canBeRemoved = item.allowedPassengers.some(passengerId =>
					travellersByIdMap.get(passengerId)?.get(item.id)
				);

				return item.canBeAdded || canBeRemoved;
			};

			return upgradeBaggage.filter(item => {
				return item.type === BaggageType.HandLuggageExcess && isCanBeAddedOrRemoved(item);
			});
		}
	);

	const getAdditionalBaggageServices = createSelector(
		getConvertedBaggageServices,
		filterBaggageServicesByTypes([BaggageType.CheckedBaggage])
	);

	const getSpecialBaggageServices = createSelector(
		getConvertedBaggageServices,
		filterBaggageServicesByTypes([
			BaggageType.SportingEquipment,
			BaggageType.PetInCabin,
			BaggageType.PetInHold,
			BaggageType.MusicalInstrument
		])
	);

	const getOverweightBaggageServices = createSelector(
		getConvertedBaggageServices,
		filterBaggageServicesByTypes([BaggageType.BaggageExcess])
	);

	const getUpgradableBaggageServices = createSelector(
		getAvailableUpgradeBaggageServices,
		getAdditionalBaggageServices,
		getSpecialBaggageServices,
		getOverweightBaggageServices,
		(...baggage) => baggage.reduce((services, items) => [...services, ...items], [])
	);

	const getMealServices = createSelector(getGdsServices, gdsServices => {
		return gdsServices.filter(
			isIncludeGdsServiceType([OrderAdditionalServiceGdsServiceServiceType.Meal])
		) as MealServiceFragment[];
	});

	const getConvertedMealServices = createSelector(getMealServices, mealServices => {
		return mealServices.map(convertMealObject);
	});

	const getMealRecommendedServices = createSelector(
		getConvertedMealServices,
		getTravellers,
		(mealServices, travellers) => {
			const recommended: RecommendedMeal[] = [];

			if (!mealServices.length) {
				return recommended;
			}

			const mealServiceMap = new Map(mealServices.map(service => [service.id, service]));

			travellers.forEach(traveller => {
				traveller.preselectedServices?.gdsServices?.services.forEach(service => {
					const meal = mealServiceMap.get(service.serviceId);

					if (meal) {
						recommended.push({
							meal,
							travellerId: traveller.id,
							segmentIds: service.segmentIds,
							count: service.count
						});
					}
				});
			});

			return recommended;
		}
	);

	const getVipServices = createSelector(getGdsServices, gdsServices => {
		return gdsServices.filter(
			isIncludeGdsServiceType([OrderAdditionalServiceGdsServiceServiceType.Vip])
		) as VipServiceFragment[];
	});

	const getBaggageDeliveryServices = createSelector(getVipServices, vipServices => {
		return vipServices.filter(isIncludeVipSubType([GdsServiceVipSubType.BaggageDelivery]));
	});

	const getPriorityBoardingServices = createSelector(getVipServices, vipServices => {
		return vipServices.filter(isIncludeVipSubType([GdsServiceVipSubType.PriorityBoarding]));
	});

	const getCapsuleHotelServices = createSelector(getVipServices, vipServices => {
		return vipServices.filter(isIncludeVipSubType([GdsServiceVipSubType.CapsuleHotel]));
	});

	const getExtraSeatServices = createSelector(getVipServices, vipServices => {
		return vipServices.filter(
			service => service.rfisc === 'EXST' && service.subType === GdsServiceVipSubType.ExtraSeat
		);
	});

	const getBusinessLoungeServices = createSelector(getGdsServices, gdsServices => {
		return gdsServices.filter(service => {
			if (service.type === OrderAdditionalServiceGdsServiceServiceType.Vip) {
				const vipService = service as VipServiceFragment;

				return (
					vipService.subType === GdsServiceVipSubType.BusinessLounge ||
					vipService.subType === GdsServiceVipSubType.BusinessLoungeInternational
				);
			}

			return service.type === OrderAdditionalServiceGdsServiceServiceType.BusinessLounge;
		});
	});

	const hasSeatServices = createSelector(getSeatMap, getSeatServices, (seatMap, seatServices) => {
		return !!seatMap.segments?.some(segment => segment.isAnySeatExistsAndFree) || !!seatServices.length;
	});

	// special services

	const getEveryPortServices = createSelector(getAdditionalServices, additionalServices => additionalServices.nas);

	const getInsurances = createSelector(getAdditionalServices, additionalServices => additionalServices.insurances);

	const getTransfer = createSelector(getAdditionalServices, additionalServices => additionalServices.transfer);

	const getAeroexpress = createSelector(getTransfer, transfer => transfer?.aeroexpress);

	// flags

	const getIsOrderNew = createSelector(
		getStatus,
		status => status === OrderStatus.New || status === CheckinOrderStatus.New
	);

	const getIsOrderBooked = createSelector(getStatus, status => status === OrderStatus.Booked);

	const getIsOrderStarted = createSelector(getStatus, status => status === CheckinOrderStatus.Started);

	const getIsOrderConfirmed = createSelector(
		getStatus,
		status => status === OrderStatus.Confirmed || status === CheckinOrderStatus.Confirmed
	);

	const getIsOrderCancelled = createSelector(getStatus, status => status === OrderStatus.Cancelled);

	const getIsOrderExpired = createSelector(getStatus, status => status === CheckinOrderStatus.Expired);

	const getIsOrderProblematic = createSelector(getStatus, status => status === OrderStatus.Problematic);

	const getIsContactsInfoEmpty = createSelector(getOrder, order => order.customer.contactInfoIsEmpty);

	const getIsTravellersDataFilled = createSelector(getOrder, order => order.isTravellersDataFilled);

	const getIsBlockedForPayment = createSelector(getOrder, order => order.isBlockedForPayment);

	const getIsSkipSeatSelectionAllowed = createSelector(
		getOrder,
		order => !isCheckoutOrder(order) && order.isSkipSeatSelectionAllowed
	);

	const getIsFfpSupported = createSelector(getFfpInfo, ffpInfo => !!ffpInfo?.ffpSupported);

	const getIsSeatMapUnavailable = createSelector(getSeatMap, getTravellers, (seatMap, travellers) => {
		const hasSegmentsWithZeroAvailableSeats = seatMap.segments?.some(segment => !segment.availableSeatsCount);
		const allTravellersHaveNotSeats = travellers.every(traveller => !traveller.services?.seats?.length);

		return hasSegmentsWithZeroAvailableSeats && allTravellersHaveNotSeats;
	});

	const getIsAllTravellersHavePreSeating = createSelector(
		getOrder,
		order => !isCheckoutOrder(order) && order.allTravellersHavePreseating
	);

	const getIsConfirmedOrPreSeating = createSelector(
		getIsOrderConfirmed,
		getIsAllTravellersHavePreSeating,
		(isConfirmed, isAllTravellersHavePreSeating) => isConfirmed && isAllTravellersHavePreSeating
	);

	const getIsAvailableSeats = createSelector(getSeatMap, seatMap => {
		return seatMap.segments?.some(segment => !!segment?.availableSeatsCount);
	});

	const getIsInsuranceSeparateStep = createSelector(getInsurances, insurances => !!insurances?.separateStep);

	const getHasRequestedOrderServices = createSelector(
		getOrder,
		getTravellers,
		getSsrData,
		(order, travellers, ssrData) => {
			const checkRequestedServices = isCheckoutOrder(order)
				? order.status === OrderStatus.Booked || order.status === OrderStatus.Confirmed
				: order.status === CheckinOrderStatus.Started;

			return checkRequestedServices && getHasRequestedServices(travellers, ssrData);
		}
	);

	return ({
		// basic
		getOrder,
		getOrderId,
		getStatus,
		getFlight,
		getSegments,
		getTravellers,
		getTravellersWithVisa,
		getTravellersNames,
		getSeatMap,
		getSeatMapAvailableSegments,
		getMessages,
		getFfpInfo,
		getFfpBonusForFlight,
		getFfpBonusByTravellers,
		// price
		getPrice,
		getPriceToPay,
		getPriceBreakdown,
		getCurrency,
		getMilesPrice,
		getSeatPrice,
		getBaggagePrice,
		getMealPrice,
		getInsurancePrice,
		getAeroexpressPrice,
		getExtrasPrice,
		getBusinessLoungePrice,
		getVipPrice,
		getAirTicketPrice,
		getChargePrice,
		getTotalPrice,
		// services
		getSelectedServices,
		getAdditionalServices,
		getGdsServices,
		getGdsServicesById,
		getSeatServices,
		getBaggageServices,
		getBaggagePetServices,
		getConvertedBaggageServices,
		getConvertedBaggageIncludedServices,
		getUpgradeBaggageServices,
		getAvailableUpgradeBaggageServices,
		getAdditionalBaggageServices,
		getSpecialBaggageServices,
		getOverweightBaggageServices,
		getUpgradableBaggageServices,
		getMealServices,
		getConvertedMealServices,
		getMealRecommendedServices,
		getVipServices,
		getBaggageDeliveryServices,
		getPriorityBoardingServices,
		getCapsuleHotelServices,
		getExtraSeatServices,
		getBusinessLoungeServices,
		hasSeatServices,
		// special services
		getEveryPortServices,
		getInsurances,
		getTransfer,
		getAeroexpress,
		// flags
		getIsOrderNew,
		getIsOrderBooked,
		getIsOrderStarted,
		getIsOrderConfirmed,
		getIsOrderCancelled,
		getIsOrderExpired,
		getIsOrderProblematic,
		getIsContactsInfoEmpty,
		getIsTravellersDataFilled,
		getIsBlockedForPayment,
		getIsSkipSeatSelectionAllowed,
		getIsFfpSupported,
		getIsSeatMapUnavailable,
		getIsAllTravellersHavePreSeating,
		getIsConfirmedOrPreSeating,
		getIsAvailableSeats,
		getIsInsuranceSeparateStep,
		getHasRequestedOrderServices
	} as unknown) as CommonOrderSelectors<T>;
}
